import {
	App,
	AppError,
	AppMenuPoint,
	AppMenuPoints,
	AppNavMenuPointsService,
	ErrorService,
	ErrorType,
	getEnvironmentFromUrl,
	LifecycleRoutes,
	MenuPointService,
	NavVariant,
	Role,
} from '@agilox/common';
import {
	CookieOverlayComponent,
	ErrorContentComponent,
	ErrorPageComponent,
	HelpContentComponent,
	ToastContainerComponent,
} from '@agilox/components';
import { NavModule, OverlayComponent } from '@agilox/ui';
import { MenuPoint, MenuPointCallbackAction } from '@agilox/ui-common';
import { AsyncPipe, LowerCasePipe } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationError, Router, RouterOutlet } from '@angular/router';
import { AuthService } from '@auth';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { UserStoreService } from '@user-store';
import { combineLatest, filter, map, Observable, shareReplay, startWith } from 'rxjs';

@Component({
	selector: 'lifecycle-root',
	templateUrl: './app.component.html',
	styleUrl: './app.component.scss',
	imports: [
		NavModule,
		RouterOutlet,
		OverlayComponent,
		ErrorContentComponent,
		ErrorPageComponent,
		HelpContentComponent,
		ToastContainerComponent,
		CookieOverlayComponent,
		AsyncPipe,
		LowerCasePipe,
		TranslatePipe,
	],
})
export class AppComponent implements OnInit {
	private menuPointService = inject(MenuPointService);
	private userStoreService = inject(UserStoreService);
	private authService = inject(AuthService);
	private translate = inject(TranslateService);
	private appNavMenuPointsService = inject(AppNavMenuPointsService);
	private router = inject(Router);
	protected errorService = inject(ErrorService);

	private role$ = this.userStoreService.role$.pipe(
		filter((role): role is Role => !!role),
		shareReplay(1)
	);

	private language$ = this.translate.onLangChange.pipe(
		startWith({ lang: this.translate.currentLang || 'en' }),
		shareReplay(1)
	);

	private roleAndLanguage$ = combineLatest([this.role$, this.language$]);

	public desktopMenuPoints$ = this.roleAndLanguage$.pipe(
		map(([role, langChange]) =>
			this.menuPointService.getMenuPointsByCategory(NavVariant.DESKTOP, role as Role)
		)
	);

	public mobileMenuPoints$ = this.roleAndLanguage$.pipe(
		map(([role, langChange]) =>
			this.menuPointService.getMenuPointsByCategory(NavVariant.MOBILE, role as Role)
		)
	);

	public appNavMenuPoints$ = this.roleAndLanguage$.pipe(
		map(([role, langChange]) => this.appNavMenuPointsService.getAppNavMenuPoints(role as Role)),
		startWith([]),
		shareReplay(1)
	);

	activeApp: AppMenuPoint | undefined = AppMenuPoints.find((app) => app.name === 'lifecycle');
	error$: Observable<AppError | null> = this.errorService.error$;
	environment: string = getEnvironmentFromUrl(LifecycleRoutes);

	helpOpen: boolean = false;

	private _routerEvents$: Observable<any> = this.router.events.pipe(
		takeUntilDestroyed(),
		shareReplay(1)
	);
	private _navigationEnd$: Observable<NavigationEnd> = this._routerEvents$.pipe(
		filter((event) => event instanceof NavigationEnd)
	);

	public pageName$ = this._navigationEnd$.pipe(
		startWith({ urlAfterRedirects: this.router.url }),
		map((event) => {
			return 'p-' + event.urlAfterRedirects.replace('/', '').replace(/\//g, '-').split('?')[0];
		})
	);

	public currentUrl$ = this._navigationEnd$.pipe(
		startWith({ urlAfterRedirects: this.router.url }),
		map((event) => event.urlAfterRedirects)
	);

	/**
	 * Will get triggered when a navigation chunk fails to load because the user is offline
	 * or when a navigation chunk fails to load because of a chunk error (e.g. chunk not found on new version)
	 */
	private _navigationError$: Observable<NavigationError> = this._routerEvents$.pipe(
		filter((event) => event instanceof NavigationError)
	);

	constructor() {
		this._navigationError$.subscribe((event) => {
			if (!navigator.onLine) {
				this.errorService.setError({
					application: App.lifecycle,
					type: ErrorType.NO_INTERNET_ERROR,
				});
			} else if (event.error.toString().includes('chunk')) {
				this.errorService.setError({
					application: App.lifecycle,
					type: ErrorType.CHUNK_ERROR,
				});
			}
		});
	}

	ngOnInit() {
		this.translate.use(this.userStoreService.currentUser()?.lang);
	}

	navCallback(menuPoint: MenuPoint): void {
		if (menuPoint.callbackAction === MenuPointCallbackAction.HELP) {
			this.helpOpen = true;
			return;
		}

		if (menuPoint.callbackAction === MenuPointCallbackAction.LOGOUT) {
			this.logout();
			return;
		}
	}

	onClose() {
		this.errorService.clearError();
	}

	onCloseHelp() {
		this.helpOpen = false;
	}

	logout() {
		this.authService.logout();
	}
}
